import Transactions from '@advisor/models/store/transactions';
import TransactionsApi from '@advisor/services/Transactions';
// eslint-disable-next-line import/extensions
import RwCustodianService from '@advisor/rwSdk/CustodianService.ts';
import exceptionHandler from '@advisor/util/exceptionHandler';
import {returnNoneApi} from '@advisor/util/returnNoneApi';
import {linkedBankAccounts} from '@advisor/util/bankLinks/getBankLinkAccounts';
import OrchestrateService from '@advisor/rwSdk/OrchestrateService';
// eslint-disable-next-line import/no-cycle
import {addRiaComponentTo} from '@advisor/util/store/paramInjects';
import RwClientService from '@advisor/rwSdk/ClientService';

// initial state
export const stateObj = function () {
  return new Transactions();
};

// actions
export const actions = {
  async getBankAccounts({commit}, payload) {
    try {
      const response = await RwCustodianService.getAllBankAccountsForClUser(payload).catch((error) => {
        throw error;
      });

      return commit('SET_BANK_ACCOUNTS', response);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.getBankAccounts', error);
    }
  },
  async getLinkedBankAccounts({commit}, payload) {
    try {
      const response = await TransactionsApi.getLinkedBankAccounts(payload);

      returnNoneApi(response);
      commit('SET_LINKED_BANK_ACCOUNTS', response.data.data);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.getLinkedBankAccounts', error);
    }
  },
  async getBrokerageAccounts({commit}, payload) {
    try {
      const response = await RwCustodianService.getCustodianAccounts(payload).catch((error) => {
        throw error;
      });

      return commit('SET_BROKERAGE_ACCOUNTS', response);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.getBrokerageAccounts', error);
    }
  },
  // RW_SDK: CustodianService deleteBankByBankAccountId
  async deleteLinkedBank(_, payload) {
    try {
      await RwCustodianService.deleteBankByBankAccountId(payload);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.deleteLinkedBank', error);
    }
  },
  async getLinkedBrokerageAccounts({commit}, payload) {
    try {
      const {clUserId, bankId} = payload;
      const options = {clUserId, bankAccountId: bankId};
      const response = await RwCustodianService.getLinkedCustodianAccountsForClUser(options).catch((error) => {
        throw error;
      });

      return commit('SET_LINKED_BROKERAGE_ACCOUNTS', response);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.getLinkedBrokerageAccounts', error);
    }
  },
  async getMoneyTransfers({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      const data = await RwCustodianService.getTransferMoneyRequests(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_TOTAL_TRANSFERS_COUNT', data);
      commit('SET_SCHEDULED_MONEY_TRANSFERS', data);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.getMoneyTransfers', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async newBankLinkRequest({commit}, payload) {
    try {
      const options = {...payload.body};
      const rdkPayload = {clUserId: payload.clUserId, ...options};
      const sdkResponse = await RwCustodianService.postBankAccountLinkRequests(rdkPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.newBankLinkRequest', error);
    }
  },
  async getBankLinkRequests({commit}, payload) {
    try {
      const sdkResponse = await RwCustodianService.getAllBankAccountLinkRequests(payload).catch((error) => {
        throw error;
      });

      return commit('SET_PENDING_REQUESTS', sdkResponse);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.getBankLinkRequests', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async newBankLinkEmailRequest({commit}, payload) {
    try {
      const response = await OrchestrateService.sendEmailToClient(payload).catch((error) => {
        throw error;
      });

      Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.newBankLinkEmailRequest', error, true);
    }
  },
  async cancelMoneyTransferRequest(_, payload) {
    try {
      const sdkResponse = await RwCustodianService.deleteMoneyTransferRequest(payload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.cancelMoneyTransferRequest', error);
    }
  },
  async clientTransactions({commit}, payload) {
    try {
      const response = await TransactionsApi.monetaryTransactions(payload);

      returnNoneApi(response);
      commit('SET_CLIENT_TRANSACTIONS', response.data);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.clientTransactions', error, true);
    }
  },
  async historyOfTransactions({commit}, payload) {
    try {
      const response = await TransactionsApi.historyOfTransactions(payload);

      returnNoneApi(response);
      commit('SET_HISTORY_TRANSACTIONS', response.data);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.historyOfTransactions', error, true);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resendDocument({commit}, payload) {
    try {
      const response = await TransactionsApi.resendDocument(payload);

      returnNoneApi(response);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.resendDocument', error, true);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async voidDocument({commit}, payload) {
    try {
      const response = await TransactionsApi.voidDocument(payload);

      returnNoneApi(response);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.voidDocument', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async advisorACATS({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.getAdvisorAutomatedAccountTransfers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CLIENT_ACATS', {data: sdkResponse});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.advisorACATS', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async clientACATS({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.getClientAutomatedAccountTransfers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CLIENT_ACATS', {data: sdkResponse});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.clientACATS', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async reSubmitACATS({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.updateClientAutomatedAccountTransfers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_RESUBMIT_RESPONSE', {data: sdkResponse});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.reSubmitACATS', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async holdingsACATS({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.saveAutomatedAccountTransferHoldings(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_HOLDINGS_RESPONSE', {data: sdkResponse});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.holdingsACATS', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async advisorACATSByStatus({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.getAdvisorAutomatedAccountTransfers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CLIENT_ACATS', {data: sdkResponse});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.advisorACATSByStatus', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async clientACATSByStatus({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwCustodianService.getClientAutomatedAccountTransfers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CLIENT_ACATS', {data: sdkResponse});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.clientACATSByStatus', error, true);
    }
  },
  async getGoalDetail({commit}, payload) {
    try {
      const sdkResponse = await RwClientService.getGoalByGoalId(payload);
      const sdkResponseData = {data: sdkResponse};

      commit('SET_GOAL_DETAIL', sdkResponseData);

      return Promise.resolve(sdkResponseData);
    } catch (error) {
      return exceptionHandler.handleGenericException('transactions.getGoalDetail', error);
    }
  },
  setSelectedPendingTransfer({commit}, payload) {
    const pendingTransfer = payload;

    commit('SET_SELECTED_PENDING_TRANSFER', pendingTransfer);
  },
  async fetchBankLinksDetails({commit}, payload) {
    try {
      const bankLinksDetails = await linkedBankAccounts(payload);

      commit('SET_BANK_LINKS_DETAILS', bankLinksDetails);
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.fetchBankLinksDetails', error);
    }
  },
  async resumeSkippedTransfer({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      await RwCustodianService.resumeTransfers(fullPayload).catch((error) => {
        throw error;
      });
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.resumeSkippedTransfer', error);
    }
  },
  async cancelScheduledTransferRequest({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      await RwCustodianService.cancelTransfer(fullPayload).catch((error) => {
        throw error;
      });
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.cancelScheduledTransferRequest', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async setSkipTransfer({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const data = await RwCustodianService.saveSkipTransfer(fullPayload).catch((error) => {
        throw error;
      });

      return data;
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.setSkipTransfer', error);
    }
  },
  async sendEmailToClient({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);

      await OrchestrateService.sendEmailToClient(fullPayload).catch((error) => {
        throw error;
      });
    } catch (error) {
      exceptionHandler.handleGenericException('transactions.sendEmailToClient', error);
    }
  },
};

// mutations
export const mutations = {
  SET_BANK_ACCOUNTS(state, payload) {
    state.bankAccounts = Array.isArray(payload) ? payload : [payload];
  },
  SET_LINKED_BANK_ACCOUNTS(state, payload) {
    state.linkedBankAccounts = Array.isArray(payload) ? payload : [payload];
  },
  SET_BROKERAGE_ACCOUNTS(state, payload) {
    state.brokerageAccounts = Array.isArray(payload) ? payload : [payload];
  },
  SET_LINKED_BROKERAGE_ACCOUNTS(state, payload) {
    state.linkedBrokerageAccounts = Array.isArray(payload) ? payload : [payload];
  },
  SET_SCHEDULED_MONEY_TRANSFERS(state, payload) {
    state.scheduledTransfers = Array.isArray(payload.data) ? payload.data : [payload.data];
  },
  SET_TOTAL_TRANSFERS_COUNT(state, payload) {
    state.totalTransfersCount = payload.count;
  },
  SET_PENDING_REQUESTS(state, payload) {
    state.pendingRequests = Array.isArray(payload) ? payload : [payload];
  },
  SET_CLIENT_TRANSACTIONS(state, payload) {
    state.clientTransactions = payload.data;
  },
  SET_HISTORY_TRANSACTIONS(state, payload) {
    state.clientTransactions = payload.data;
  },
  SET_ESIGNATURES(state, payload) {
    state.eSignatures = payload.data;
  },
  SET_CLIENT_ACATS(state, payload) {
    state.clientAcats = payload.data;
  },
  SET_RESUBMIT_RESPONSE(state, payload) {
    const {acatRequestId} = payload.data;

    state.resubmitResponse = acatRequestId;
  },
  SET_HOLDINGS_RESPONSE(state, payload) {
    state.holdingsAcats = payload.data;
  },
  SET_GOAL_DETAIL(state, payload) {
    state.goalDetail = payload.data;
  },
  SET_SELECTED_PENDING_TRANSFER(state, payload) {
    state.selectedPendingTransfer = payload;
  },
  SET_BANK_LINKS_DETAILS(state, payload) {
    state.bankLinksDetails = payload;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  actions,
  mutations,
};
